import * as v from 'valibot';

import {
  cargoTypeSchema,
  carrierSchema,
  contractStatusSchema,
  countrySchema,
  customerSchema,
  equipmentTypeSchema,
  optionSchema,
  pricingCategorySchema,
  pricingTypeSchema,
  supplierDiversitySchema,
  tariffTypeSchema,
  transportationModeSchema,
} from '@/modules/entities';

const nullableNumber = v.nullish(v.number());
const nullableString = v.nullish(v.string());

export const contractApiSchema = v.object({
  approvedBy: nullableNumber,
  approvedOn: nullableString,
  cargoType: nullableString,
  carrierKey: nullableNumber,
  createdBy: nullableNumber,
  createdOn: nullableString,
  customerId: nullableNumber,
  customerLaneId: nullableNumber,
  deletedBy: nullableNumber,
  deletedOn: nullableString,
  effectiveFrom: nullableString,
  effectiveTo: nullableString,
  key: v.string(),
  modifiedBy: nullableNumber,
  modifiedOn: nullableString,
  name: v.string(),
  pricingBasis: nullableString,
  pricingCategory: nullableString,
  pricingType: nullableString,
  primaryDocumentKey: nullableString,
  serviceLayer: nullableString,
  status: nullableString,
  supplierDiversity: nullableString,
  trailerKey: nullableNumber,
  transportationMode: nullableString,
  uniqueName: nullableString,
});

export const contractsApiSchema = v.object({
  items: v.array(contractApiSchema),
  totalCount: v.number(),
});

export const createContractApiSchema = v.omit(contractApiSchema, [
  'approvedBy',
  'approvedOn',
  'createdBy',
  'createdOn',
  'deletedBy',
  'deletedOn',
  'key',
  'modifiedBy',
  'modifiedOn',
]);

export type ContractApi = v.InferOutput<typeof contractApiSchema>;
export type ContractsApi = v.InferOutput<typeof contractsApiSchema>;
export type CreateContractApi = v.InferInput<typeof createContractApiSchema>;

/**
 * Query params for GET /api/pricing/contract/search.
 */
export const contractsSearchParamsSchema = v.object({
  approvedOn: v.optional(v.pipe(v.string(), v.isoTimestamp())),
  cargoType: v.optional(v.string()),
  carrierKey: v.optional(v.number()),
  createdBy: v.optional(v.number()),
  customerKey: v.optional(v.number()),
  customerLaneId: v.optional(v.number()),
  destinationCity: v.optional(v.string()),
  destinationCountry: v.optional(v.string()),
  destinationPortName: v.optional(v.string()),
  destinationPortType: v.optional(v.number()),
  destinationPostalCode: v.optional(v.string()),
  destinationState: v.optional(v.string()),
  effectiveFrom: v.optional(v.pipe(v.string(), v.isoTimestamp())),
  effectiveTo: v.optional(v.pipe(v.string(), v.isoTimestamp())),
  modifiedBy: v.optional(v.number()),
  name: v.optional(v.string()),
  originCity: v.optional(v.string()),
  originCountry: v.optional(v.string()),
  originPortName: v.optional(v.string()),
  originPortType: v.optional(v.number()),
  originPostalCode: v.optional(v.string()),
  originState: v.optional(v.string()),
  pricingBasis: v.optional(v.string()),
  pricingCategory: v.optional(v.string()),
  pricingType: v.optional(v.string()),
  serviceKey: v.optional(v.number()),
  skip: v.optional(v.number()),
  sorting: v.optional(v.string()),
  status: v.optional(v.string()),
  supplierDiversity: v.optional(v.string()),
  take: v.optional(v.number()),
  trailerKey: v.optional(v.number()),
  transportationMode: v.optional(v.string()),
  uniqueName: v.optional(v.string()),
});

export type ContractsSearchParams = v.InferInput<typeof contractsSearchParamsSchema>;

/**
 * Contracts search filters form
 */
export const contractsSearchFiltersFormSchema = v.pipe(
  v.object({
    approvedOn: v.optional(v.pipe(v.string(), v.isoTimestamp())),
    cargoType: v.optional(v.nullable(optionSchema)),
    carrierKey: v.optional(v.nullable(optionSchema)),
    contractName: v.optional(v.nullable(v.string())),
    createdBy: v.optional(v.nullable(optionSchema)),
    customerLaneId: v.optional(v.nullable(v.string())),
    destinationCity: v.optional(v.nullable(v.string())),
    destinationCountry: v.optional(v.nullable(countrySchema)),
    destinationPortName: v.optional(v.nullable(v.string())),
    destinationPortType: v.optional(v.nullable(optionSchema)),
    destinationPostalCode: v.optional(v.nullable(v.string())),
    destinationState: v.optional(v.nullable(optionSchema)),
    effectiveFrom: v.optional(v.pipe(v.string(), v.isoTimestamp('Please enter a valid date'))),
    effectiveTo: v.optional(v.pipe(v.string(), v.isoTimestamp('Please enter a valid date'))),
    modifiedBy: v.optional(v.nullable(optionSchema)),
    originCity: v.optional(v.nullable(v.string())),
    originCountry: v.optional(v.nullable(countrySchema)),
    originPortName: v.optional(v.nullable(v.string())),
    originPortType: v.optional(v.nullable(optionSchema)),
    originPostalCode: v.optional(v.nullable(v.string())),
    originState: v.optional(v.nullable(optionSchema)),
    pricingBasis: v.optional(v.nullable(optionSchema)),
    pricingCategory: v.optional(v.nullable(optionSchema)),
    pricingType: v.optional(v.nullable(optionSchema)),
    serviceKey: v.optional(v.nullable(optionSchema)),
    status: v.optional(v.nullable(optionSchema)),
    supplierDiversity: v.optional(v.nullable(optionSchema)),
    trailerKey: v.optional(v.nullable(optionSchema)),
    transportationMode: v.optional(v.nullable(optionSchema)),
    uniqueName: v.optional(v.nullable(v.string())),
  }),
  v.forward(
    v.check((data) => {
      if (!data.effectiveFrom || !data.effectiveTo) {
        return true;
      }

      return new Date(data.effectiveTo) > new Date(data.effectiveFrom);
    }, 'To date should be bigger than From date'),
    ['effectiveTo'],
  ),
);

export type ContractsSearchFiltersFormValues = v.InferInput<typeof contractsSearchFiltersFormSchema>;

export const contractSchema = v.object({
  approvedBy: v.nullish(v.number()),
  approvedOn: v.nullish(v.string()),
  cargoType: v.nullish(cargoTypeSchema),
  carrier: v.nullish(carrierSchema),
  createdBy: v.nullish(v.number()),
  createdOn: v.nullish(v.string()),
  customer: v.nullish(customerSchema),
  customerLaneId: v.nullish(optionSchema),
  deletedBy: v.nullish(v.number()),
  deletedOn: v.nullish(v.string()),
  effectiveFrom: v.optional(v.pipe(v.string(), v.isoTimestamp('Please enter a valid date'))),
  effectiveTo: v.optional(v.pipe(v.string(), v.isoTimestamp('Please enter a valid date'))),
  key: v.string(),
  modifiedBy: v.nullish(v.number()),
  modifiedOn: v.nullish(v.string()),
  name: v.pipe(v.string(), v.nonEmpty('Contract name is required')),
  pricingBasis: v.nullish(tariffTypeSchema),
  pricingCategory: v.nullish(pricingCategorySchema),
  pricingType: v.nullish(pricingTypeSchema),
  primaryDocumentKey: v.nullish(v.string()),
  serviceLayer: v.nullish(optionSchema),
  status: v.nullish(v.string()),
  supplierDiversity: v.nullish(supplierDiversitySchema),
  trailer: v.nullish(equipmentTypeSchema),
  transportationMode: v.nullish(transportationModeSchema),
  uniqueName: v.pipe(v.string(), v.nonEmpty('Contract number is required')),
});

export const newContractSchema = v.omit(contractSchema, [
  'approvedBy',
  'approvedOn',
  'createdBy',
  'createdOn',
  'deletedBy',
  'deletedOn',
  'key',
  'modifiedBy',
  'modifiedOn',
]);

export const contractsSchema = v.object({
  items: v.array(contractSchema),
  totalCount: v.number(),
});

export type Contract = v.InferOutput<typeof contractSchema>;
export type Contracts = v.InferOutput<typeof contractsSchema>;
export type NewContract = v.InferOutput<typeof newContractSchema>;

export const changeContractStatusFormSchema = v.object({
  comment: v.nullish(v.string()),
  status: v.pipe(
    v.nullable(contractStatusSchema),
    v.check((value) => value !== null, 'Contract status is required'),
  ),
});

export type ChangeContractStatusFormValues = v.InferOutput<typeof changeContractStatusFormSchema>;

export type ChangeContractStatusParams = {
  comment?: string | null;
  contractKey: string;
  status: string;
};

export const getDefaultNewContract = (): NewContract => ({
  ...v.getDefaults(newContractSchema),
  name: '',
  uniqueName: '',
});
